// src/shared/components/layout/LandingLayout.tsx
import { useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { ThemeProvider } from '../../../core/providers/ThemeProvider';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { Footer } from './Footer/Footer';
import { HeroSection } from '../sections/hero/HeroSection';
import { getUserTypeFromContext } from '../../utils/userTypeUtils';
import { useUserStore } from '../../../app/store/useUserStore';
import { isSenderRole, isTravellerRole } from '../../utils/roleUtils';
import type { LandingLayoutProps } from './types';
import { Box, Typography } from '@mui/material';
import { createMuiTheme } from '../../styles/theme/theme';
import { FloatingActionButton } from '../ui/FloatingActionButton/FloatingActionButton';

export const LandingLayout: React.FC<LandingLayoutProps> = ({
  children,
  userType: propUserType,
  showHeader = true,
  showFooter = true,
  showFAB = false
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth0();
  const { user } = useUserStore();

  // Prioritize user.user_role from store to ensure role changes are reflected immediately
  const userType = useMemo(() => {
    // For authenticated users, always use their actual role from the store
    if (isAuthenticated && user?.user_role) {
      return user.user_role;
    }
    // For unauthenticated users, use prop or context
    return propUserType
      || getUserTypeFromContext(location.pathname, location.state as any);
  }, [isAuthenticated, user?.user_role, propUserType, location.pathname, location.state]);

  // Create MUI theme based on user role - must be called before any early returns
  const muiTheme = useMemo(() => createMuiTheme(userType || 'sender'), [userType]);

  // Determine if FAB should be shown - for both sender and traveller
  const shouldShowFAB =
    showFAB &&
    isAuthenticated &&
    (isSenderRole(user?.user_role) || isTravellerRole(user?.user_role));

  useEffect(() => {
    if (!userType) {
      const timer = setTimeout(() => {
        navigate('/', {
          replace: true,
          state: {
            message: 'Please try again. We could not determine your user type.'
          }
        });
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [userType, navigate]);

  if (!userType) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh'
        }}
      >
        <Typography>Redirecting...</Typography>
      </Box>
    );
  }

  return (
    <MuiThemeProvider theme={muiTheme}>
      <ThemeProvider userType={userType}>
        <div className="min-h-screen flex flex-col" style={{ position: 'relative' }}>
          {showHeader && (
            <Box component="header" sx={{ position: 'relative' }}>
              {/* Hero Section with integrated header */}
              <HeroSection />
            </Box>
          )}

          <main className="flex-1">
            {children}
          </main>

          {showFooter && <Footer userType={userType} />}

          {/* Floating Action Button */}
          {shouldShowFAB && <FloatingActionButton />}
        </div>
      </ThemeProvider>
    </MuiThemeProvider>
  );
};

LandingLayout.displayName = 'LandingLayout';